import React, { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Dimensions, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../../components/LhToastContainer";
import {
  getBackfillWorkbench, confirmFulfillment, updateFulfillment, submitSiteAccessories,
  updateAccessories, approveAccessories, submitRedistribution, submitReceipt, markDelivered,
  BackfillWorkbench, BackfillLineView, AccessoryChecklistItem, AccessoryItem,
  AccessorySubmitItem, RedistributionLine, RedistributionPayload,
} from "../../../api/equipmentReviewApi";
import SingleSelectDropdown from "./SingleSelectDropdown";
import { SHIPMENT_CARRIERS, toShipmentCarrier } from "../util/shipmentCarrier";

const DESKTOP_BP = 900;
const isDK = () => Dimensions.get("window").width >= DESKTOP_BP;
const fmt = (iso?: string|null) => {
  if (!iso) return ""; try { return new Date(iso).toLocaleString(undefined,{month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"}); } catch { return iso; }
};

const STAGES = [
  "Awaiting fulfillment review",
  "Awaiting site accessory selection",
  "Awaiting accessory validation",
  "Awaiting vendor shipment",
  "Replacement in transit",
  "Awaiting site receipt",
  "Complete",
];

interface Props { ticketId: number; onMutated: () => void; }

const BackfillWorkbenchCard: React.FC<Props> = ({ ticketId, onMutated }) => {
  const toast = useToast();
  const [wb, setWb] = useState<BackfillWorkbench|null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<Record<string,boolean>>({});
  const [dk, setDk] = useState(isDK());
  useEffect(() => {
    const s = Dimensions.addEventListener("change", () => setDk(isDK()));
    return () => s?.remove?.();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getBackfillWorkbench(ticketId);
    setLoading(false);
    if (r.notFound) return; // no backfill yet
    if (r.error) { toast.show(r.error, "error"); return; }
    setWb(r.workbench);
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const isBusy = (k: string) => !!submitting[k];
  const withBusy = async (k: string, fn: () => Promise<void>) => {
    if (isBusy(k)) return;
    setSubmitting(s => ({...s,[k]:true}));
    try { await fn(); } finally { setSubmitting(s => { const n={...s}; delete n[k]; return n; }); }
  };
  const apply = (w: BackfillWorkbench, msg: string) => {
    setWb(w); if (msg) toast.show(msg, "success"); onMutated();
  };

  if (loading) return <View style={b.card}><ActivityIndicator size="small" color="#7871E2" style={{margin:20}}/></View>;
  if (!wb) return null;

  // Sites only see card when their step is active
  if (wb.viewer === "SITE" && !wb.visibleStep) return null;

  const curIdx = STAGES.findIndex(s => s.toLowerCase() === wb.stage.toLowerCase());

  return (
    <View style={b.card}>
      <View style={b.hdr}>
        <MaterialIcons name="swap-horiz" size={16} color="#7871E2"/>
        <Text style={b.hdrTitle}>Spare Pool Backfill</Text>
        <Text style={b.stagePill}>{wb.stage}</Text>
      </View>
      {wb.header ? <Text style={b.headerInfo}>{wb.header}</Text> : null}
      <StageTracker stages={STAGES} currentIdx={curIdx}/>
      {wb.visibleStep==="FULFILLMENT_REVIEW" && <FulfillmentPanel wb={wb} isBusy={isBusy} withBusy={withBusy} apply={apply} ticketId={ticketId} dk={dk}/>}
      {wb.visibleStep==="SITE_ACCESSORIES" && <SiteAccessoriesPanel wb={wb} isBusy={isBusy} withBusy={withBusy} apply={apply} ticketId={ticketId}/>}
      {wb.visibleStep==="ACCESSORY_VALIDATION" && <AccessoryValidationPanel wb={wb} isBusy={isBusy} withBusy={withBusy} apply={apply} ticketId={ticketId}/>}
      {wb.visibleStep==="VENDOR_REDISTRIBUTION" && <RedistributionPanel wb={wb} isBusy={isBusy} withBusy={withBusy} apply={apply} ticketId={ticketId} dk={dk}/>}
      {wb.visibleStep==="SITE_RECEIPT" && <SiteReceiptPanel wb={wb} isBusy={isBusy} withBusy={withBusy} apply={apply} ticketId={ticketId}/>}
    </View>
  );
};

const StageTracker: React.FC<{stages:string[];currentIdx:number}> = ({stages,currentIdx}) => (
  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={b.trackerScroll} contentContainerStyle={b.trackerRow}>
    {stages.map((s,i) => (
      <View key={s} style={b.trackerItem}>
        <View style={[b.trackerDot, i<currentIdx&&b.trackerDotDone, i===currentIdx&&b.trackerDotActive]}>
          {i<currentIdx?<MaterialIcons name="check" size={12} color="#fff"/>:<Text style={b.trackerNum}>{i+1}</Text>}
        </View>
        <Text style={[b.trackerLabel, i===currentIdx&&b.trackerLabelActive]} numberOfLines={2}>{s}</Text>
        {i<stages.length-1&&<View style={[b.trackerLine,i<currentIdx&&b.trackerLineDone]}/>}
      </View>
    ))}
  </ScrollView>
);

const FulfillmentPanel: React.FC<{wb:BackfillWorkbench;isBusy:(k:string)=>boolean;withBusy:(k:string,fn:()=>Promise<void>)=>Promise<void>;apply:(w:BackfillWorkbench,m:string)=>void;ticketId:number;dk:boolean}> = ({wb,isBusy,withBusy,apply,ticketId,dk}) => {
  const toast = useToast();
  const lines = wb.lines??[];
  const [skuDrafts,setSkuDrafts] = useState<Record<number,string>>(() => Object.fromEntries(lines.map(l=>[l.lineId,l.fulfillmentSku])));
  const handleUpdate = async () => withBusy("ful-upd", async () => {
    const changed = lines.filter(l=>skuDrafts[l.lineId]&&skuDrafts[l.lineId]!==l.fulfillmentSku).map(l=>({lineId:l.lineId,fulfillmentSku:skuDrafts[l.lineId]}));
    if (!changed.length) return;
    const r = await updateFulfillment(ticketId,changed);
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  const handleConfirm = async () => withBusy("ful-con", async () => {
    const r = await confirmFulfillment(ticketId);
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  return (
    <View style={b.panel}>
      <Text style={b.panelTitle}>Fulfillment Review</Text>
      {!dk&&<View style={b.mobileBanner}><MaterialIcons name="desktop-mac" size={14} color="#005CBC"/><Text style={b.mobileBannerTxt}>Fulfillment changes are available on desktop.</Text></View>}
      {lines.map(line=>(
        <View key={line.lineId} style={b.lineRow}>
          <View style={b.lineInfo}>
            <Text style={b.lineSku}>{line.requestedSku}</Text>
            <Text style={b.lineDesc}>{line.requestedDescription}</Text>
            <Text style={b.lineMeta}>{line.requestedQuantity} unit(s) · {line.availableInPool} in pool</Text>
          </View>
          {dk
            ?<TextInput style={b.skuInput} value={skuDrafts[line.lineId]??""} onChangeText={v=>setSkuDrafts(d=>({...d,[line.lineId]:v}))} placeholder="Fulfillment SKU"/>
            :<Text style={b.lineDesc}>{line.fulfillmentSku}</Text>
          }
        </View>
      ))}
      {dk&&<View style={b.btnRow}>
        <Pressable style={[b.btnSecondary,isBusy("ful-upd")&&b.btnDis]} onPress={handleUpdate} disabled={isBusy("ful-upd")}><Text style={b.btnSecTxt}>Save Changes</Text></Pressable>
        <Pressable style={[b.btnPrimary,isBusy("ful-con")&&b.btnDis]} onPress={handleConfirm} disabled={isBusy("ful-con")}>{isBusy("ful-con")?<ActivityIndicator size="small" color="#fff"/>:<Text style={b.btnPriTxt}>Confirm &amp; Reserve</Text>}</Pressable>
      </View>}
    </View>
  );
};

const SiteAccessoriesPanel: React.FC<{wb:BackfillWorkbench;isBusy:(k:string)=>boolean;withBusy:(k:string,fn:()=>Promise<void>)=>Promise<void>;apply:(w:BackfillWorkbench,m:string)=>void;ticketId:number}> = ({wb,isBusy,withBusy,apply,ticketId}) => {
  const toast = useToast();
  const checklist = wb.accessoryChecklist??[];
  const [selected,setSelected] = useState<Record<string,number>>({});
  const toggle = (sku:string) => setSelected(s => s[sku]?({...s,[sku]:undefined} as any):{...s,[sku]:1});
  const setQty = (sku:string,q:number) => setSelected(s=>({...s,[sku]:q}));
  const handleSubmit = async () => withBusy("site-acc", async () => {
    const accessories: AccessorySubmitItem[] = Object.entries(selected).filter(([,q])=>q&&(q as number)>0).map(([sku,qty])=>({sku,quantity:qty as number}));
    const r = await submitSiteAccessories(ticketId,accessories);
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  return (
    <View style={b.panel}>
      <Text style={b.panelTitle}>Select Accessories</Text>
      {checklist.map(item=>(
        <View key={item.sku} style={b.accRow}>
          <Pressable style={[b.checkbox,!!selected[item.sku]&&b.checkboxOn]} onPress={()=>toggle(item.sku)}>
            {!!selected[item.sku]&&<MaterialIcons name="check" size={14} color="#fff"/>}
          </Pressable>
          <View style={{flex:1}}><Text style={b.accDesc}>{item.description}</Text><Text style={b.accType}>{item.equipmentType}</Text></View>
          {!!selected[item.sku]&&<TextInput style={b.qtyInput} value={String(selected[item.sku]??1)} onChangeText={v=>{const n=parseInt(v,10);if(!isNaN(n)&&n>0)setQty(item.sku,n);}} keyboardType="numeric"/>}
        </View>
      ))}
      <View style={b.btnRow}>
        <Pressable style={[b.btnPrimary,isBusy("site-acc")&&b.btnDis]} onPress={handleSubmit} disabled={isBusy("site-acc")}><Text style={b.btnPriTxt}>Submit to Sustain</Text></Pressable>
      </View>
    </View>
  );
};

const AccessoryValidationPanel: React.FC<{wb:BackfillWorkbench;isBusy:(k:string)=>boolean;withBusy:(k:string,fn:()=>Promise<void>)=>Promise<void>;apply:(w:BackfillWorkbench,m:string)=>void;ticketId:number}> = ({wb,isBusy,withBusy,apply,ticketId}) => {
  const toast = useToast();
  const accs = wb.accessories??[];
  const [draft,setDraft] = useState<AccessoryItem[]>([...accs]);
  const setQty = (sku:string,q:number) => setDraft(d=>d.map(a=>a.sku===sku?{...a,quantity:q}:a));
  const remove = (sku:string) => setDraft(d=>d.filter(a=>a.sku!==sku));
  const handleAmend = async () => withBusy("acc-amend", async () => {
    const r = await updateAccessories(ticketId, draft.map(a=>({sku:a.sku,quantity:a.quantity})));
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  const handleApprove = async () => withBusy("acc-app", async () => {
    const r = await approveAccessories(ticketId);
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  return (
    <View style={b.panel}>
      <Text style={b.panelTitle}>Accessory Validation</Text>
      {draft.map(a=>(
        <View key={a.sku} style={b.accRow}>
          <View style={{flex:1}}>
            <Text style={b.accDesc}>{a.description}</Text>
            <View style={[b.addedByBadge,a.addedBy==="SITE"&&b.addedBySite]}>
              <Text style={b.addedByTxt}>{a.addedBy==="SITE"?"Requested by site":"Added by Sustain"}</Text>
            </View>
          </View>
          <TextInput style={b.qtyInput} value={String(a.quantity)} onChangeText={v=>{const n=parseInt(v,10);if(!isNaN(n)&&n>0)setQty(a.sku,n);}} keyboardType="numeric"/>
          <Pressable onPress={()=>remove(a.sku)} style={b.removeBtn}><MaterialIcons name="close" size={16} color="#C12C01"/></Pressable>
        </View>
      ))}
      {draft.length===0&&<Text style={b.emptyTxt}>No accessories selected by site.</Text>}
      <View style={b.btnRow}>
        <Pressable style={[b.btnSecondary,isBusy("acc-amend")&&b.btnDis]} onPress={handleAmend} disabled={isBusy("acc-amend")}><Text style={b.btnSecTxt}>Save Amendments</Text></Pressable>
        <Pressable style={[b.btnPrimary,isBusy("acc-app")&&b.btnDis]} onPress={handleApprove} disabled={isBusy("acc-app")}><Text style={b.btnPriTxt}>Approve &amp; Forward to Vendor</Text></Pressable>
      </View>
    </View>
  );
};

const RedistributionPanel: React.FC<{wb:BackfillWorkbench;isBusy:(k:string)=>boolean;withBusy:(k:string,fn:()=>Promise<void>)=>Promise<void>;apply:(w:BackfillWorkbench,m:string)=>void;ticketId:number;dk:boolean}> = ({wb,isBusy,withBusy,apply,ticketId,dk}) => {
  const toast = useToast();
  const lines = wb.lines??[];
  const submitted = !!wb.shipment;
  const [carrier,setCarrier]=useState(toShipmentCarrier(wb.shipment?.carrier));
  const [tracking,setTracking]=useState(wb.shipment?.trackingNumber??"");
  const [boxes,setBoxes]=useState(String(wb.shipment?.boxCount??""));
  const [weight,setWeight]=useState(String(wb.shipment?.weight??""));
  const [lineData,setLineData]=useState<Record<number,{qty:string;subSku:string;serials:string}>>(() =>
    Object.fromEntries(lines.map(l=>([l.lineId,{qty:String(l.requestedQuantity),subSku:l.vendorSubstituteSku??"",serials:l.serialNumbers??""}])))
  );
  const setLine=(lineId:number,k:string,v:string)=>setLineData(d=>({...d,[lineId]:{...d[lineId],[k]:v}}));
  const handleSubmit = async () => {
    if (!carrier) { toast.show("Select a carrier before submitting the redistribution.","warning"); return; }
    if (!tracking.trim()) { toast.show("Tracking number is required before submitting the redistribution.","warning"); return; }
    const bx=parseFloat(boxes); const wt=parseFloat(weight);
    if (boxes&&isNaN(bx)) { toast.show("Box count must be a number.","warning"); return; }
    if (weight&&isNaN(wt)) { toast.show("Weight must be a decimal number.","warning"); return; }
    withBusy("redist", async () => {
      const payload: RedistributionPayload = {
        carrier, trackingNumber:tracking,
        ...(boxes?{boxCount:bx}:{}),
        ...(weight?{weight:wt}:{}),
        lines: lines.map(l => {
          const d=lineData[l.lineId];
          return {lineId:l.lineId,...(d.qty?{sendingQuantity:parseInt(d.qty,10)}:{}),...(d.subSku?{substituteSku:d.subSku}:{}),...(d.serials?{serialNumbers:d.serials}:{})};
        }),
      };
      const r = await submitRedistribution(ticketId,payload);
      if (r.error) { toast.show(r.error,"error"); return; }
      if (r.workbench) apply(r.workbench, r.message);
    });
  };
  return (
    <View style={b.panel}>
      <Text style={b.panelTitle}>Vendor Redistribution</Text>
      {wb.replacementPoNumber&&<View style={b.infoBnr}><Text style={b.infoBnrTxt}>Ship against PO: {wb.replacementPoNumber}</Text></View>}
      {!dk&&<View style={b.mobileBanner}><MaterialIcons name="desktop-mac" size={14} color="#005CBC"/><Text style={b.mobileBannerTxt}>Fulfillment changes are available on desktop.</Text></View>}
      {submitted&&<View style={b.infoBnr}><Text style={b.infoBnrTxt}>✓ Redistribution submitted. {wb.shipment?.carrier} {wb.shipment?.trackingNumber}</Text></View>}
      {!submitted&&(<>
        {lines.map(l=>(
          <View key={l.lineId} style={b.lineRow}>
            <Text style={b.lineSku}>{l.requestedSku}</Text>
            <Text style={b.lineDesc}>{l.requestedDescription}</Text>
            <Text style={b.lineMeta}>Requested: {l.requestedQuantity}</Text>
            <View style={b.redist2Col}>
              <View><Text style={b.fieldLabel}>Sending Qty</Text><TextInput style={b.smInput} value={lineData[l.lineId]?.qty??""} onChangeText={v=>setLine(l.lineId,"qty",v)} keyboardType="numeric"/></View>
              <View><Text style={b.fieldLabel}>Substitute SKU</Text><TextInput style={b.smInput} value={lineData[l.lineId]?.subSku??""} onChangeText={v=>setLine(l.lineId,"subSku",v)} placeholder="Leave blank for like-for-like"/></View>
            </View>
            <Text style={b.fieldLabel}>Serial Numbers (one per line)</Text>
            <TextInput style={b.serialsInput} value={lineData[l.lineId]?.serials??""} onChangeText={v=>setLine(l.lineId,"serials",v)} multiline placeholder="SN001\nSN002"/>
          </View>
        ))}
        <Text style={[b.sectionTitle,{marginTop:12}]}>Shipment Details</Text>
        <View style={b.shipGrid}>
          <View><SingleSelectDropdown label="Carrier" value={carrier} options={SHIPMENT_CARRIERS} emptyLabel="Select carrier" allowEmpty={false} onChange={setCarrier}/></View>
          <View><Text style={b.fieldLabel}>Tracking # <Text style={{color:"#C12C01"}}>*</Text></Text><TextInput style={b.smInput} value={tracking} onChangeText={setTracking}/></View>
          <View><Text style={b.fieldLabel}>Boxes</Text><TextInput style={b.smInput} value={boxes} onChangeText={setBoxes} keyboardType="numeric"/></View>
          <View><Text style={b.fieldLabel}>Weight (lbs)</Text><TextInput style={b.smInput} value={weight} onChangeText={setWeight} keyboardType="decimal-pad"/></View>
        </View>
        <View style={b.btnRow}>
          <Pressable style={[b.btnPrimary,isBusy("redist")&&b.btnDis]} onPress={handleSubmit} disabled={isBusy("redist")}><Text style={b.btnPriTxt}>Submit Redistribution</Text></Pressable>
        </View>
      </>)}
    </View>
  );
};

const SiteReceiptPanel: React.FC<{wb:BackfillWorkbench;isBusy:(k:string)=>boolean;withBusy:(k:string,fn:()=>Promise<void>)=>Promise<void>;apply:(w:BackfillWorkbench,m:string)=>void;ticketId:number}> = ({wb,isBusy,withBusy,apply,ticketId}) => {
  const toast = useToast();
  const [condition,setCondition]=useState<"Good"|"Damaged"|"Incomplete">("Good");
  const [notes,setNotes]=useState("");
  const submitted = !!wb.receipt;
  const handleSubmit = async () => withBusy("receipt", async () => {
    const r = await submitReceipt(ticketId,{condition,notes:notes||undefined});
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  const handleMarkDelivered = async () => withBusy("delivered", async () => {
    const r = await markDelivered(ticketId);
    if (r.error) { toast.show(r.error,"error"); return; }
    if (r.workbench) apply(r.workbench, r.message);
  });
  const serials = (wb.lines??[]).filter(l=>l.serialNumbers).map(l=>l.serialNumbers!.split(/[,;\t\n]+/).filter(Boolean));
  return (
    <View style={b.panel}>
      <Text style={b.panelTitle}>Site Receipt</Text>
      {wb.shipment&&(
        <View style={b.infoBnr}>
          <Text style={b.infoBnrTxt}>Carrier: {wb.shipment.carrier} · Tracking: {wb.shipment.trackingNumber}</Text>
        </View>
      )}
      {serials.flat().length>0&&(
        <View style={b.serialChips}>
          {serials.flat().map(sn=><View key={sn} style={b.serialChip}><Text style={b.serialChipTxt}>{sn}</Text></View>)}
        </View>
      )}
      {submitted?(
        <View>
          <View style={[b.infoBnr,{backgroundColor:"#DDF9DA"}]}>
            <Text style={[b.infoBnrTxt,{color:"#166534"}]}>✓ Received · {wb.receipt!.condition} · {wb.receipt!.receivedDate}</Text>
          </View>
          {wb.receipt!.notes?<Text style={b.notesDisplay}>{wb.receipt!.notes}</Text>:null}
        </View>
      ):(<>
        <Text style={b.panelTitle}>Condition</Text>
        <View style={b.condRow}>
          {(["Good","Damaged","Incomplete"] as const).map(c=>(
            <Pressable key={c} style={[b.condChip,condition===c&&b.condChipActive]} onPress={()=>setCondition(c)}>
              <Text style={[b.condChipTxt,condition===c&&{color:"#fff"}]}>{c}</Text>
            </Pressable>
          ))}
        </View>
        <Text style={b.fieldLabel}>Notes (optional)</Text>
        <TextInput style={b.serialsInput} value={notes} onChangeText={setNotes} multiline maxLength={1000} placeholder="Any damage or missing items..."/>
        <View style={b.btnRow}>
          <Pressable style={[b.btnPrimary,isBusy("receipt")&&b.btnDis]} onPress={handleSubmit} disabled={isBusy("receipt")}><Text style={b.btnPriTxt}>Mark Received</Text></Pressable>
        </View>
      </>)}
      {wb.viewer==="SUSTAIN"&&wb.stage.toLowerCase().includes("transit")&&!submitted&&(
        <Pressable style={[b.btnSecondary,{marginTop:8},isBusy("delivered")&&b.btnDis]} onPress={handleMarkDelivered} disabled={isBusy("delivered")}>
          <Text style={b.btnSecTxt}>Mark Delivered (optional)</Text>
        </Pressable>
      )}
    </View>
  );
};

const b = StyleSheet.create({
  card:{backgroundColor:"#fff",borderRadius:12,borderWidth:1,borderColor:"#DDDFFD",marginBottom:16,overflow:"hidden"},
  hdr:{flexDirection:"row",alignItems:"center",gap:8,backgroundColor:"#F0EFFE",paddingHorizontal:20,paddingVertical:14},
  hdrTitle:{fontSize:14,fontWeight:"700",color:"#7871E2",flex:1},
  stagePill:{fontSize:11,fontWeight:"700",color:"#7871E2",backgroundColor:"#E1E3FD",borderRadius:12,paddingHorizontal:10,paddingVertical:3},
  headerInfo:{fontSize:12,color:"#6b7280",paddingHorizontal:20,paddingVertical:10,fontStyle:"italic"},
  trackerScroll:{paddingVertical:14,paddingHorizontal:16},
  trackerRow:{flexDirection:"row",alignItems:"flex-start",paddingBottom:4},
  trackerItem:{alignItems:"center",minWidth:80,position:"relative",marginRight:0},
  trackerDot:{width:24,height:24,borderRadius:12,backgroundColor:"#e5e7eb",alignItems:"center",justifyContent:"center",marginBottom:6},
  trackerDotDone:{backgroundColor:"#027C06"},trackerDotActive:{backgroundColor:"#7871E2"},
  trackerNum:{fontSize:10,fontWeight:"700",color:"#9ca3af"},
  trackerLabel:{fontSize:10,color:"#9ca3af",textAlign:"center",maxWidth:74,lineHeight:14},
  trackerLabelActive:{color:"#7871E2",fontWeight:"700"},
  trackerLine:{position:"absolute",top:12,left:"60%" as any,right:"-60%" as any,height:2,backgroundColor:"#e5e7eb"},
  trackerLineDone:{backgroundColor:"#027C06"},
  panel:{paddingHorizontal:20,paddingVertical:16,borderTopWidth:1,borderTopColor:"#f0f0f0"},
  panelTitle:{fontSize:14,fontWeight:"700",color:"#374151",marginBottom:12},
  sectionTitle:{fontSize:13,fontWeight:"700",color:"#374151",marginBottom:8},
  lineRow:{backgroundColor:"#f8f9fa",borderRadius:8,borderWidth:1,borderColor:"#e5e7eb",padding:14,marginBottom:10},
  lineSku:{fontSize:12,fontWeight:"700",color:"#005CBC"},
  lineDesc:{fontSize:13,fontWeight:"600",color:"#111827",marginTop:2},
  lineMeta:{fontSize:12,color:"#6b7280",marginTop:2,marginBottom:8},
  skuInput:{borderWidth:1,borderColor:"#d1d5db",borderRadius:6,padding:8,fontSize:13,color:"#111827",backgroundColor:"#fff",marginTop:6},
  accRow:{flexDirection:"row",alignItems:"center",gap:10,padding:10,borderRadius:8,backgroundColor:"#f8f9fa",borderWidth:1,borderColor:"#e5e7eb",marginBottom:8},
  checkbox:{width:24,height:24,borderRadius:4,borderWidth:2,borderColor:"#7871E2",alignItems:"center",justifyContent:"center"},
  checkboxOn:{backgroundColor:"#7871E2"},
  accDesc:{fontSize:13,fontWeight:"600",color:"#111827"},accType:{fontSize:11,color:"#6b7280"},
  qtyInput:{width:52,borderWidth:1,borderColor:"#d1d5db",borderRadius:6,padding:6,fontSize:13,textAlign:"center",backgroundColor:"#fff"},
  addedByBadge:{marginTop:4,paddingHorizontal:8,paddingVertical:2,borderRadius:4,backgroundColor:"#e8f0fb",alignSelf:"flex-start"},
  addedBySite:{backgroundColor:"#fef3c7"},addedByTxt:{fontSize:10,fontWeight:"700",color:"#374151"},
  removeBtn:{padding:4},
  emptyTxt:{fontSize:13,color:"#9ca3af",marginBottom:12},
  redist2Col:{flexDirection:"row",gap:12,marginTop:8,marginBottom:8},
  shipGrid:{flexDirection:"row",flexWrap:"wrap",gap:10,marginBottom:12},
  smInput:{borderWidth:1,borderColor:"#d1d5db",borderRadius:6,padding:8,fontSize:13,backgroundColor:"#fff",minWidth:120},
  serialsInput:{borderWidth:1,borderColor:"#d1d5db",borderRadius:6,padding:10,fontSize:13,backgroundColor:"#fff",minHeight:70,marginTop:4},
  fieldLabel:{fontSize:11,fontWeight:"700",color:"#6b7280",marginTop:8,marginBottom:4},
  infoBnr:{backgroundColor:"#e8f0fb",borderRadius:6,padding:10,marginBottom:10},
  infoBnrTxt:{fontSize:12,color:"#005CBC"},
  serialChips:{flexDirection:"row",flexWrap:"wrap",gap:6,marginBottom:10},
  serialChip:{backgroundColor:"#e8f0fb",borderRadius:4,paddingHorizontal:8,paddingVertical:3},
  serialChipTxt:{fontSize:11,color:"#034895",fontFamily:Platform.OS==="ios"?"Courier":"monospace"},
  condRow:{flexDirection:"row",gap:8,marginBottom:12,flexWrap:"wrap"},
  condChip:{paddingHorizontal:14,paddingVertical:8,borderRadius:20,borderWidth:1.5,borderColor:"#d1d5db"},
  condChipActive:{backgroundColor:"#034895",borderColor:"#034895"},condChipTxt:{fontSize:13,fontWeight:"700",color:"#374151"},
  notesDisplay:{fontSize:12,color:"#6b7280",fontStyle:"italic",marginTop:6},
  mobileBanner:{flexDirection:"row",alignItems:"center",gap:6,backgroundColor:"#e8f0fb",borderRadius:6,padding:10,marginBottom:10},
  mobileBannerTxt:{fontSize:12,color:"#005CBC"},
  btnRow:{flexDirection:"row",justifyContent:"flex-end",gap:8,marginTop:14},
  btnPrimary:{flexDirection:"row",alignItems:"center",gap:6,backgroundColor:"#034895",borderRadius:20,paddingHorizontal:20,paddingVertical:10},
  btnPriTxt:{fontSize:13,fontWeight:"700",color:"#fff"},
  btnSecondary:{flexDirection:"row",alignItems:"center",borderWidth:1.5,borderColor:"#034895",borderRadius:20,paddingHorizontal:16,paddingVertical:8},
  btnSecTxt:{fontSize:13,fontWeight:"700",color:"#034895"},
  btnDis:{opacity:0.45},
});

export default BackfillWorkbenchCard;


